import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import SchoolPicker from '../components/SchoolPicker';
import apiService from '../services/api';
import {
  promotionConfirmText,
  promotionNotice,
  promotionWarnings,
} from '../utils/staffPromotion';
import '../styles/StaffPages.css';

const createEmptyRow = () => ({
  first_name: '',
  last_name: '',
  email: '',
  school_name: '',
});

const namePattern = /^[A-Za-z][A-Za-z' -]*$/;

const createStaffAdminRowSchema = z.object({
  first_name: z.string().trim().min(1, 'First name is required').max(150, 'First name is too long').regex(namePattern, 'First name can contain only letters, spaces, apostrophes, and hyphens'),
  last_name: z.string().trim().min(1, 'Last name is required').max(150, 'Last name is too long').regex(namePattern, 'Last name can contain only letters, spaces, apostrophes, and hyphens'),
  email: z.string().trim().email('Email must be valid'),
  school_name: z.string().trim().min(1, 'Please select a school'),
});

const createStaffAdminsPayloadSchema = z.object({
  items: z.array(createStaffAdminRowSchema).min(1, 'At least one account is required'),
});

const fieldLabels = {
  first_name: 'First Name',
  last_name: 'Last Name',
  email: 'Email',
  school_name: 'School',
  form: 'Form',
};

const surfaceStyle = {
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '12px',
  padding: '16px',
};

const sectionTitleStyle = {
  margin: 0,
  fontSize: '18px',
  fontWeight: 600,
  color: '#111827',
};

const mutedTextStyle = {
  color: '#6b7280',
  fontSize: '14px',
};

const secondaryButtonStyle = {
  background: '#f3f4f6',
  color: '#111827',
  border: '1px solid #d1d5db',
};

const dangerButtonStyle = {
  background: '#fee2e2',
  color: '#b91c1c',
  border: '1px solid #fecaca',
};

const formatUsDateTime = (value) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).format(date);
};

const SuperuserLinkGenerator = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schools, setSchools] = useState([]);
  const [toast, setToast] = useState(null);
  const [sendingEmail, setSendingEmail] = useState({});
  const [sendingAllEmails, setSendingAllEmails] = useState(false);
  const [sentEmail, setSentEmail] = useState({});
  const [createdAccounts, setCreatedAccounts] = useState([]);
  const [collapsedFormRows, setCollapsedFormRows] = useState({});
  const [historyCollapsed, setHistoryCollapsed] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({ school_name: '', search: '' });
  const [historyRows, setHistoryRows] = useState([]);
  const [collapsedHistoryRows, setCollapsedHistoryRows] = useState({});
  const [formRows, setFormRows] = useState([createEmptyRow()]);
  const [creationErrors, setCreationErrors] = useState([]);
  // Accounts that already existed when the form was submitted. Reported by the
  // server, never acted on until someone clicks Promote on the row.
  const [promotableRows, setPromotableRows] = useState([]);
  const [promotedAccounts, setPromotedAccounts] = useState([]);
  const [promoting, setPromoting] = useState({});
  const navigate = useNavigate();

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  React.useEffect(() => {
    const load = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        const schoolsResponse = await apiService.getSchools();
        const schoolsList = Array.isArray(schoolsResponse)
          ? schoolsResponse
          : (schoolsResponse?.results || []);
        setSchools(schoolsList);
        const historyResponse = await apiService.getCreatedStaffAdminsHistorySuperuser({});
        setHistoryRows(historyResponse?.history || []);
      } catch (error) {
        navigate('/login/');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const loadHistory = async (overrides = {}, silent = false) => {
    const nextFilters = { ...historyFilters, ...overrides };
    if (!silent) {
      setHistoryLoading(true);
    }
    try {
      const params = {};
      if (nextFilters.school_name) {
        params.school_name = nextFilters.school_name;
      }
      if (nextFilters.search) {
        params.search = nextFilters.search;
      }
      const response = await apiService.getCreatedStaffAdminsHistorySuperuser(params);
      setHistoryRows(response?.history || []);
      setCollapsedHistoryRows({});
    } catch (error) {
      if (!silent) {
        showToast('Failed to load history', 'error');
      }
    } finally {
      if (!silent) {
        setHistoryLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setCreatedAccounts([]);
    setCreationErrors([]);
    setPromotableRows([]);
    setPromotedAccounts([]);
    try {
      const payload = {
        items: formRows.map((row) => ({
          first_name: row.first_name.trim(),
          last_name: row.last_name.trim(),
          email: row.email.trim(),
          school_name: row.school_name.trim(),
        })),
      };
      const parseResult = createStaffAdminsPayloadSchema.safeParse(payload);
      if (!parseResult.success) {
        const formattedErrors = parseResult.error.issues.reduce((acc, issue) => {
          const rowIndex = typeof issue.path[1] === 'number' ? issue.path[1] : 0;
          const field = typeof issue.path[2] === 'string' ? issue.path[2] : 'form';
          const existing = acc.find(entry => entry.index === rowIndex);
          if (existing) {
            existing.errors[field] = [issue.message];
          } else {
            acc.push({ index: rowIndex, errors: { [field]: [issue.message] } });
          }
          return acc;
        }, []);
        setCreationErrors(formattedErrors);
        showToast('Please fix validation errors', 'error');
        setSaving(false);
        return;
      }
      const resp = await apiService.generateAdminLinkSuperuser(payload);
      const results = resp?.results || [];
      const errors = resp?.errors || [];
      const promotable = resp?.promotable || [];
      setCreatedAccounts(results);
      setSentEmail({});
      setCreationErrors(errors);
      setPromotableRows(promotable);
      await loadHistory({}, true);
      if (results.length > 0) {
        showToast(
          promotable.length > 0
            ? `Created ${results.length} account(s) · ${promotable.length} already exist`
            : `Created ${results.length} account(s)`
        );
      } else if (promotable.length > 0) {
        // Not a failure: these accounts exist and are waiting to be promoted.
        showToast(`${promotable.length} account(s) already exist — promote them below`);
      } else {
        showToast('No accounts were created', 'error');
      }
    } catch (error) {
      const data = error?.response?.data;
      const msg = data?.items?.[0] || data?.error || 'Failed to generate admin link';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleRowChange = (index, key, value) => {
    setFormRows((prev) => prev.map((row, rowIndex) => (
      rowIndex === index ? { ...row, [key]: value } : row
    )));
  };

  const addRow = () => {
    setFormRows((prev) => [...prev, createEmptyRow()]);
  };

  const removeRow = (index) => {
    setFormRows((prev) => {
      if (prev.length === 1) {
        return prev;
      }
      return prev.filter((_, rowIndex) => rowIndex !== index);
    });
    setCollapsedFormRows((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  // `promoted` picks the email that states no password. A promoted account keeps
  // its own, and the credentials email would state one that does not work.
  const sendEmail = async (email, { promoted = false } = {}) => {
    setSendingEmail((prev) => ({ ...prev, [email]: true }));
    try {
      if (promoted) {
        await apiService.sendStaffAccessGrantedEmailSuperuser(email);
      } else {
        await apiService.sendStaffAccountCreatedEmailSuperuser(email);
      }
      setSentEmail((prev) => ({ ...prev, [email]: true }));
      setHistoryRows((prev) => prev.map((row) => {
        if ((row?.created_user?.email || '').toLowerCase() !== (email || '').toLowerCase()) {
          return row;
        }
        return {
          ...row,
          email_sent: true,
          email_sent_at: new Date().toISOString(),
        };
      }));
      showToast(`Email sent to ${email}`);
    } catch (error) {
      const msg = error?.response?.data?.error || 'Failed to send email';
      showToast(msg, 'error');
    } finally {
      setSendingEmail((prev) => ({ ...prev, [email]: false }));
    }
  };

  const sendEmailToAllCreated = async () => {
    const emails = createdAccounts
      .map((entry) => entry?.user?.email)
      .filter(Boolean);
    if (emails.length === 0) {
      return;
    }

    setSendingAllEmails(true);
    const nextSendingState = emails.reduce((acc, email) => ({ ...acc, [email]: true }), {});
    setSendingEmail((prev) => ({ ...prev, ...nextSendingState }));

    let successCount = 0;
    for (const email of emails) {
      try {
        await apiService.sendStaffAccountCreatedEmailSuperuser(email);
        successCount += 1;
        setSentEmail((prev) => ({ ...prev, [email]: true }));
        setHistoryRows((prev) => prev.map((row) => {
          if ((row?.created_user?.email || '').toLowerCase() !== email.toLowerCase()) {
            return row;
          }
          return {
            ...row,
            email_sent: true,
            email_sent_at: new Date().toISOString(),
          };
        }));
      } catch (error) {
        showToast(`Failed to send email to ${email}`, 'error');
      } finally {
        setSendingEmail((prev) => ({ ...prev, [email]: false }));
      }
    }

    setSendingAllEmails(false);
    showToast(`Sent ${successCount} of ${emails.length} emails`);
  };

  const promoteRow = async (row) => {
    const userId = row?.user?.id;
    const schoolName = row?.requested_school?.name;
    if (!userId || !schoolName) {
      return;
    }
    // The deliberate second step: nothing above this line has changed the
    // account, and the text spells out what confirming will change.
    if (!window.confirm(promotionConfirmText(row))) {
      return;
    }

    setPromoting((prev) => ({ ...prev, [userId]: true }));
    try {
      const resp = await apiService.promoteStaffUsersSuperuser({
        items: [{ user_id: userId, school_name: schoolName }],
      });
      const promotedRow = (resp?.results || [])[0];
      if (!promotedRow) {
        const rowError = (resp?.errors || [])[0]?.errors || {};
        const firstMessage = Object.values(rowError)[0];
        showToast(
          Array.isArray(firstMessage) ? firstMessage[0] : (firstMessage || 'Could not promote that account'),
          'error'
        );
        return;
      }
      setPromotedAccounts((prev) => [...prev, promotedRow]);
      setPromotableRows((prev) => prev.filter((entry) => entry?.user?.id !== userId));
      await loadHistory({}, true);
      showToast(`${row.email} now has staff access`);
    } catch (error) {
      const msg = error?.response?.data?.error || 'Failed to promote that account';
      showToast(msg, 'error');
    } finally {
      setPromoting((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const toggleFormRowCollapsed = (index) => {
    setCollapsedFormRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const toggleHistoryRowCollapsed = (key) => {
    setCollapsedHistoryRows((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
  };

  const handleHistorySearch = async (e) => {
    e.preventDefault();
    await loadHistory();
  };

  // Companion to getRowFieldError for the rows that are not errors: the account
  // exists, which is a state to act on rather than a mistake to fix.
  const getRowPromotionNotice = (rowIndex) =>
    promotionNotice(promotableRows.find((entry) => entry.index === rowIndex));

  const getRowFieldError = (rowIndex, fieldName) => {
    const rowError = creationErrors.find((entry) => entry.index === rowIndex);
    if (!rowError || !rowError.errors) {
      return '';
    }
    const value = rowError.errors[fieldName];
    if (!value) {
      return '';
    }
    return Array.isArray(value) ? (value[0] || '') : String(value);
  };

  if (loading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />
          <h1 className="staff-page-header">Staff User Links</h1>
          <p className="staff-page-subheader">Create an admin user and generate login credentials</p>

          {toast && (
            <div className={`toast toast-${toast.type || 'success'}`}>{toast.message}</div>
          )}

          <form onSubmit={handleSubmit} style={{ marginTop: '20px', width: '100%' }}>
            {formRows.map((row, index) => (
              <div key={`row-${index}`} style={{ ...surfaceStyle, marginBottom: '12px', padding: '14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ fontWeight: 600, color: '#111827' }}>
                    User {index + 1}: {row.email || 'New account'}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    {index > 0 && (
                      <button type="button" className="primary-button" onClick={() => removeRow(index)} style={dangerButtonStyle}>
                        Remove
                      </button>
                    )}
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" onClick={() => toggleFormRowCollapsed(index)} style={{ cursor: 'pointer', color: '#546E7A', transform: collapsedFormRows[index] ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }}><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </div>
                </div>

                {!collapsedFormRows[index] && (
                  <>
                    <div className="form-group">
                      <label className="form-label">First Name</label>
                      <input
                        className="form-input"
                        type="text"
                        value={row.first_name}
                        onChange={e => handleRowChange(index, 'first_name', e.target.value)}
                        required
                      />
                      {getRowFieldError(index, 'first_name') && (
                        <div style={{ marginTop: '6px', color: '#b91c1c', fontSize: '12px' }}>
                          {getRowFieldError(index, 'first_name')}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Last Name</label>
                      <input
                        className="form-input"
                        type="text"
                        value={row.last_name}
                        onChange={e => handleRowChange(index, 'last_name', e.target.value)}
                        required
                      />
                      {getRowFieldError(index, 'last_name') && (
                        <div style={{ marginTop: '6px', color: '#b91c1c', fontSize: '12px' }}>
                          {getRowFieldError(index, 'last_name')}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Email</label>
                      <input
                        className="form-input"
                        type="email"
                        value={row.email}
                        onChange={e => handleRowChange(index, 'email', e.target.value)}
                        required
                      />
                      {getRowFieldError(index, 'email') && (
                        <div style={{ marginTop: '6px', color: '#b91c1c', fontSize: '12px' }}>
                          {getRowFieldError(index, 'email')}
                        </div>
                      )}
                      {/* Amber, not red: the account existing is a state to act
                          on further down the page, not a mistake in this row. */}
                      {getRowPromotionNotice(index) && (
                        <div style={{ marginTop: '6px', color: '#b45309', fontSize: '12px' }}>
                          {getRowPromotionNotice(index)}
                        </div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">School Name</label>
                      <SchoolPicker
                        schools={schools}
                        value={schools.find((school) => school.name === row.school_name) || null}
                        onChange={(school) => handleRowChange(index, 'school_name', school?.name || '')}
                      />
                      {getRowFieldError(index, 'school_name') && (
                        <div style={{ marginTop: '6px', color: '#b91c1c', fontSize: '12px' }}>
                          {getRowFieldError(index, 'school_name')}
                        </div>
                      )}
                    </div>

                  </>
                )}
              </div>
            ))}

            <div style={{ marginTop: '16px' }}>
              <button className="primary-button" type="button" onClick={addRow} style={{ ...secondaryButtonStyle, marginRight: '8px' }}>
                Add Another
              </button>
              <button className="primary-button" type="submit" disabled={saving}>
                {saving ? 'Generating...' : formRows.length === 1 ? 'Generate Account' : 'Generate Accounts'}
              </button>
            </div>
          </form>

          {creationErrors.some((entry) => Object.prototype.hasOwnProperty.call(entry.errors || {}, 'form')) && (
            <div style={{ marginTop: '16px', color: '#b91c1c', fontSize: '13px' }}>
              {creationErrors
                .filter((entry) => Object.prototype.hasOwnProperty.call(entry.errors || {}, 'form'))
                .map((entry) => `${fieldLabels.form}: ${Array.isArray(entry.errors.form) ? entry.errors.form.join(', ') : String(entry.errors.form)}`)
                .join(' | ')}
            </div>
          )}

          {promotableRows.length > 0 && (
            <div style={{ marginTop: '24px', width: '100%' }}>
              <div style={{ ...surfaceStyle }}>
                <div style={{ marginBottom: '10px' }}>
                  <h2 style={sectionTitleStyle}>Existing Accounts</h2>
                  <div style={mutedTextStyle}>
                    These emails already have HomeCrowd accounts, so nothing was created for
                    them. Promoting one grants staff access to the school selected on the
                    form and leaves their password alone.
                  </div>
                </div>
                <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Name</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Email</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>School</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>What Changes</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promotableRows.map((entry) => {
                        const userId = entry?.user?.id;
                        // The stored name, so it is obvious whose account this is
                        // rather than whose name was typed into the form.
                        const fullName = `${entry?.user?.first_name || ''} ${entry?.user?.last_name || ''}`.trim();
                        const warnings = promotionWarnings(entry);
                        const alreadyStaff = (entry?.warnings || []).includes('already_staff');
                        return (
                          <tr key={userId || entry.email}>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>{fullName || '—'}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>{entry.email}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>
                              {entry?.current_school?.name || 'None'}
                              {' → '}
                              <span style={{ fontWeight: 600 }}>{entry?.requested_school?.name || '—'}</span>
                            </td>
                            <td style={{ ...{ padding: '10px', borderBottom: '1px solid #f3f4f6' }, fontSize: '12px', color: '#6b7280' }}>
                              {entry.can_promote
                                ? (warnings.length > 0
                                    ? warnings.map((line) => <div key={line}>{line}</div>)
                                    : 'Gains staff access. Password unchanged.')
                                : <span style={{ color: '#b91c1c' }}>{entry.blocked_message || 'Cannot be promoted.'}</span>}
                            </td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>
                              <button
                                className="primary-button"
                                type="button"
                                onClick={() => promoteRow(entry)}
                                disabled={!entry.can_promote || Boolean(promoting[userId])}
                                style={secondaryButtonStyle}
                              >
                                {promoting[userId]
                                  ? 'Promoting...'
                                  : alreadyStaff ? 'Update School' : 'Promote to Staff'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {promotedAccounts.length > 0 && (
            <div style={{ marginTop: '24px', width: '100%' }}>
              <div style={{ ...surfaceStyle }}>
                <div style={{ marginBottom: '10px' }}>
                  <h2 style={sectionTitleStyle}>Promoted Accounts</h2>
                  <div style={mutedTextStyle}>
                    These accounts now have staff access. There is no password to send —
                    they keep the one they already sign in with.
                  </div>
                </div>
                <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Name</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Email</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>School</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Password</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Email Status</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {promotedAccounts.map((entry) => {
                        const email = entry?.user?.email;
                        const fullName = `${entry?.user?.first_name || ''} ${entry?.user?.last_name || ''}`.trim();
                        return (
                          <tr key={email}>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>{fullName || '—'}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>{email}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>{entry?.school?.name || '—'}</td>
                            <td style={{ ...{ padding: '10px', borderBottom: '1px solid #f3f4f6' }, color: '#9ca3af' }}>
                              — <span style={{ fontSize: '12px' }}>(unchanged)</span>
                            </td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>
                              <span style={{ color: sentEmail[email] ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                                {sentEmail[email] ? 'Sent' : 'Not Sent'}
                              </span>
                            </td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>
                              <button
                                className="primary-button"
                                type="button"
                                onClick={() => sendEmail(email, { promoted: true })}
                                disabled={Boolean(sendingEmail[email])}
                                style={secondaryButtonStyle}
                              >
                                {sendingEmail[email] ? 'Sending...' : (sentEmail[email] ? 'Resend Email' : 'Send Email')}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {createdAccounts.length > 0 && (
            <div style={{ marginTop: '24px', width: '100%' }}>
              <div style={{ ...surfaceStyle }}>
                <div style={{ marginBottom: '10px' }}>
                  <h2 style={sectionTitleStyle}>Newly Created Accounts</h2>
                  <div style={mutedTextStyle}>Review generated credentials and send account emails.</div>
                </div>
                <div style={{ marginBottom: '10px' }}>
                  <button
                    className="primary-button"
                    type="button"
                    onClick={sendEmailToAllCreated}
                    disabled={sendingAllEmails || createdAccounts.length === 0}
                    style={secondaryButtonStyle}
                  >
                    {sendingAllEmails ? 'Sending to all...' : 'Send Email to All'}
                  </button>
                </div>
                <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb' }}>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Name</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Email</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>School</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Password</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Email Status</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {createdAccounts.map((entry) => {
                        const email = entry?.user?.email;
                        const fullName = `${entry?.user?.first_name || ''} ${entry?.user?.last_name || ''}`.trim();
                        return (
                          <tr key={email}>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>{fullName || '—'}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>{email}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>{entry?.school?.name || '—'}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>{entry?.generated_password}</td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>
                              <span style={{ color: sentEmail[email] ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                                {sentEmail[email] ? 'Sent' : 'Not Sent'}
                              </span>
                            </td>
                            <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>
                              <button
                                className="primary-button"
                                type="button"
                                onClick={() => sendEmail(email)}
                                disabled={Boolean(sendingEmail[email])}
                                style={secondaryButtonStyle}
                              >
                                {sendingEmail[email] ? 'Sending...' : (sentEmail[email] ? 'Resend Email' : 'Send Email')}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: '32px', width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h2 style={sectionTitleStyle}>Created Staff Admins History</h2>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" onClick={() => setHistoryCollapsed((prev) => !prev)} style={{ cursor: 'pointer', color: '#546E7A', transform: historyCollapsed ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }}><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </div>

            {!historyCollapsed && (
              <>
                <div style={{ ...surfaceStyle, marginBottom: '12px' }}>
                  <form onSubmit={handleHistorySearch} style={{ display: 'flex', gap: '8px', alignItems: 'end', flexWrap: 'wrap' }}>
                    <div className="form-group" style={{ minWidth: '240px', marginBottom: 0 }}>
                      <label className="form-label">School</label>
                      <SchoolPicker
                        schools={schools}
                        value={schools.find((school) => school.name === historyFilters.school_name) || null}
                        onChange={(school) => setHistoryFilters((prev) => ({ ...prev, school_name: school?.name || '' }))}
                        allOptionLabel="All schools"
                        allSelected={!historyFilters.school_name}
                      />
                    </div>

                    <div className="form-group" style={{ minWidth: '260px', marginBottom: 0 }}>
                      <label className="form-label">Search</label>
                      <input
                        className="form-input"
                        type="text"
                        placeholder="Name or email"
                        value={historyFilters.search}
                        onChange={(e) => setHistoryFilters((prev) => ({ ...prev, search: e.target.value }))}
                      />
                    </div>

                    <button type="submit" className="primary-button" disabled={historyLoading} style={secondaryButtonStyle}>
                      {historyLoading ? 'Loading...' : 'Apply Filters'}
                    </button>
                  </form>
                </div>

                <div style={{ ...surfaceStyle }}>
                  <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb' }}>
                          <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>User</th>
                          <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Email</th>
                          <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>School</th>
                          <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Email Status</th>
                          <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Created At</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Details</th>
                        <th style={{ textAlign: 'left', padding: '10px', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {historyRows.map((row) => {
                          const rowKey = row.id;
                          const isCollapsed = collapsedHistoryRows[rowKey] !== false;
                          const name = `${row?.created_user?.first_name || ''} ${row?.created_user?.last_name || ''}`.trim();
                          return (
                            <React.Fragment key={rowKey}>
                              <tr>
                                <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>{name || '—'}</td>
                                <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>{row?.created_user?.email || '—'}</td>
                                <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>{row?.school || '—'}</td>
                                <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>
                                  <span style={{ color: row?.email_sent ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                                    {row?.email_sent ? 'Sent' : 'Not Sent'}
                                  </span>
                                </td>
                              <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>{formatUsDateTime(row?.created_at)}</td>
                                <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" onClick={() => toggleHistoryRowCollapsed(rowKey)} style={{ cursor: 'pointer', color: '#546E7A', transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }}><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                </td>
                              <td style={{ padding: '10px', borderBottom: '1px solid #f3f4f6' }}>
                                <button
                                  type="button"
                                  className="primary-button"
                                  onClick={() => sendEmail(row?.created_user?.email)}
                                  disabled={Boolean(sendingEmail[row?.created_user?.email])}
                                  style={secondaryButtonStyle}
                                >
                                  {sendingEmail[row?.created_user?.email]
                                    ? 'Sending...'
                                    : (row?.email_sent ? 'Resend Email' : 'Send Email')}
                                </button>
                              </td>
                              </tr>
                              {!isCollapsed && (
                                <tr>
                                <td colSpan={7} style={{ padding: '10px', backgroundColor: '#f9fafb', borderBottom: '1px solid #f3f4f6' }}>
                                    <div className="staff-page-content">Created By: {row?.created_by || '—'}</div>
                                    <div className="staff-page-content">Source: {row?.source || '—'}</div>
                                  <div className="staff-page-content">Email Sent At: {formatUsDateTime(row?.email_sent_at)}</div>
                                    <div className="staff-page-content">User ID: {row?.created_user?.id || '—'}</div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {historyRows.length === 0 && (
                          <tr>
                          <td colSpan={7} style={{ padding: '12px', color: '#6b7280' }}>
                              No history found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperuserLinkGenerator;
